import type {
  ClientAccountTypeEnum,
  ClientConfirmReservationMutation,
  PackageDetailFragmentFragment,
  PromotionDestinationTypeEnum,
  SignInProviderEnum,
} from "@/__generated__/graphql";
import { getAdsProperties, getGALinkerParam } from "@/lib/identifiers";
import { rudderanalytics } from "@/lib/rudderstack";
import { isLocal } from "@/lib/utils";
import type { AirconServiceName, BetaServiceName, ServiceName } from "@/types/service";

import type { SubscriptionEventTracking } from "@luce/customer-app";

import { logAnalyticEvent, setAnalyticUserId } from "../../firebase";
import { AppsFlyerAnalytics } from "../appsflyer";
import { trackHotjarEvent } from "../hotjar";
import { PosthogAnalytics } from "../posthog";

export type AnalyticsConversion =
  | EventSharerReferralClick
  | EventPageView
  | EventPurchase
  | EventAddToCart
  | EventFindAvailability
  | EventUserSignUp
  | EventUserSignIn
  | EventViewService
  | EventViewBetaService
  | EventWAClick
  | EventAppDownloadButton
  | EventRebookClick
  | EventRebookComplete
  | EventViewRecommendedService
  | EventSelectServiceCreditAmount
  | EventInitiateShareGiftCard
  | EventShareGiftCard
  | EventInitiateGiftCard
  | EventSubmitGiftCard
  | EventUpsellCredit
  | EventBannerCtaClicked
  | SubscriptionEventTracking;

export type ServiceCreditType = "MASSAGE" | "HOME_BEAUTY" | "PET_CARE";

type Item = {
  item_id: string;
  item_category: string;
  price: number;
  quantity: number;
};

type EventRebookClick = {
  name: "rebook_click" | "rebook_widget" | "rebook_visit";
  values?: Record<string, string>;
};

type EventPageView = {
  name: "page_view";
  values?: Record<string, string>;
};

type EventWAClick = {
  name: "wa_sales_button_click";
  values?: Record<string, string>;
};

type EventSharerReferralClick = {
  name: "sharer_referral_click";
  values?: {
    clientId: string;
    clientName: string;
  };
};

type EventPurchase = {
  name: "purchase";
  values: {
    currency: "SGD";
    value: number;
    transaction_id: string;
    coupon?: string;
    tax?: number; // we can put GST for this
    items: Item[];
    campaign_id?: string | null;
    is_first_booking?: boolean;
  };
};

type EventRebookComplete = {
  name: "rebook_complete";
  values: EventPurchase["values"] & {
    source_visit_id?: string;
    source_package_id?: string;
  };
};

type EventAddToCart = {
  name: "add_to_cart";
  values: {
    currency: "SGD";
    value: number;
    items: Item[];
  };
};

type EventFindAvailability = {
  name: "find_availability";
  values: {
    currency: "SGD";
    value: number;
    items: Item[];
  };
};

type EventUserSignUp = {
  name: "user_sign_up";
  values: {
    method: SignInProviderEnum;
    path: string;
  };
};

type EventUserSignIn = {
  name: "user_sign_in";
  values: {
    method: SignInProviderEnum;
    path: string;
  };
};

type EventViewService = {
  name: "view_service";
  values: {
    // service_type: PackageDepartmentEnum;
    service_type: ServiceName | AirconServiceName;
  };
};
type EventViewBetaService = {
  name: "view_beta_service";
  values: {
    // service_type: PackageDepartmentEnum;
    service_type: BetaServiceName;
  };
};
type EventViewRecommendedService = {
  name: "view_recommended_service";
  values: {
    service_type: ServiceName | BetaServiceName;
  };
};

type EventAppDownloadButton = {
  name: "app_download_button_click";
  values?: Record<string, string>;
};

type EventSelectServiceCreditAmount = {
  name: "select_service_credit_amount";
  values: {
    credit_type: ServiceCreditType;
    amount: number;
    currency: "SGD";
    invoice_id: string;
    campaign_id?: string | null;
  };
};

type EventInitiateShareGiftCard = {
  name: "initiate_share_gift_card";
  values?: never;
};

type EventShareGiftCard = {
  name: "share_gift_card";
  values: {
    type: "whatsapp" | "telegram" | "messenger" | "sms" | "copy" | "other";
  };
};

type EventInitiateGiftCard = {
  name: "initiate_gift_card";
  values: {
    account_type: ClientAccountTypeEnum;
  };
};

type EventSubmitGiftCard = {
  name: "submit_gift_card";
  values: {
    account_type: ClientAccountTypeEnum;
    currency: "SGD";
    value: number;
    invoice_id?: string;
    campaign_id?: string | null;
  };
};
type EventBannerCtaClicked = {
  name: "banner_cta_clicked";
  values: {
    campaign_id?: string | null;
    promo_title: string;
    button_position: number;
    button_label: string;
    destination_type: PromotionDestinationTypeEnum;
    destination_screen?: string;
    is_logged_in: boolean;
  };
};

type EventUpsellCredit = {
  name: "upsell_credit";
  values: {
    credit_type: "MASSAGE" | "HOME_BEAUTY" | "PET_CARE";
  };
};

function toAppsFlyerContent(items: Item[]) {
  return {
    af_content_id: items.map((item) => item.item_id),
    af_content_type: items[0]?.item_category,
    af_quantity: items.reduce((total, item) => total + item.quantity, 0),
    af_content: items,
  };
}

type AppsFlyerEvent = { eventName: string; eventValues?: Record<string, unknown> };

/**
 * AppsFlyer intentionally carries only the money funnel, so most event types map
 * to no events (they stay in Firebase + RudderStack).
 */
function toAppsFlyerEvents(eventData: AnalyticsConversion): AppsFlyerEvent[] {
  switch (eventData.name) {
    case "user_sign_in":
      return [{ eventName: "af_login" }];
    case "user_sign_up":
      return [
        {
          eventName: "af_complete_registration",
          eventValues: { af_registration_method: eventData.values.method },
        },
      ];
    case "find_availability":
      return [
        {
          eventName: "af_find_availability",
          eventValues: {
            af_price: eventData.values.value,
            af_currency: eventData.values.currency,
            ...toAppsFlyerContent(eventData.values.items),
          },
        },
      ];
    case "add_to_cart":
      return [
        {
          eventName: "af_add_to_cart",
          eventValues: {
            af_price: eventData.values.value,
            af_currency: eventData.values.currency,
            ...toAppsFlyerContent(eventData.values.items),
          },
        },
      ];
    case "purchase": {
      const isFirstBooking = eventData.values.is_first_booking ?? false;
      const eventValues = {
        af_revenue: eventData.values.value,
        af_currency: eventData.values.currency,
        af_order_id: eventData.values.transaction_id,
        promo_code: eventData.values.coupon,
        is_first_booking: isFirstBooking,
        ...toAppsFlyerContent(eventData.values.items),
      };

      const events: AppsFlyerEvent[] = [{ eventName: "af_purchase", eventValues }];
      if (isFirstBooking) {
        events.push({ eventName: "first_purchase", eventValues });
      }
      return events;
    }
    default:
      return [];
  }
}

export async function sendAnalyticData(eventData: AnalyticsConversion): Promise<void> {
  if (isLocal) return;

  const properties = await getAdsProperties();

  trackHotjarEvent(eventData.name);
  PosthogAnalytics.track(eventData.name, eventData.values);
  logAnalyticEvent(eventData.name, {
    ...eventData.values,
    timestamp: Date.now(),
  });

  rudderanalytics.track(eventData.name, {
    ...eventData.values,
    ...properties,
  });

  for (const { eventName, eventValues } of toAppsFlyerEvents(eventData)) {
    AppsFlyerAnalytics.logEvent(eventName, eventValues);
  }
}

export function setAnalyticUser({
  userId,
  email,
  firstName,
}: {
  userId: string;
  email: string;
  firstName: string;
}) {
  if (isLocal) return;

  setAnalyticUserId(userId);
  PosthogAnalytics.setUserProperties(userId, email, firstName);
  rudderanalytics.identify(userId, {
    email,
    name: firstName,
    // AppsFlyer attribution labels carried to RudderStack for consistency
    ...(AppsFlyerAnalytics.getAttribution() ?? {}),
  });
  AppsFlyerAnalytics.setUserId(userId);
}

export function resetAnalyticUser() {
  if (isLocal) return;

  setAnalyticUserId(null);
  PosthogAnalytics.reset();
  rudderanalytics.reset();
  AppsFlyerAnalytics.reset();
}

export function transformBookingDataToAddToCart(
  packageDetails: PackageDetailFragmentFragment[],
): EventAddToCart["values"] {
  const totalServiceBillingValue = packageDetails.reduce<number>(
    (total, { serviceBillingValue }) => total + serviceBillingValue,
    0,
  );

  return {
    currency: "SGD",
    value: totalServiceBillingValue * (packageDetails[0].repeatEveryTimes || 1),
    items: packageDetails.map((packageDetail) => ({
      item_id: packageDetail.id,
      item_category: packageDetail.department,
      price: packageDetail.unitValue,
      quantity: packageDetail.units * (packageDetail.repeatEveryTimes || 1),
    })),
  };
}

export function transformReservationDataToPurchase(
  multiplier: number,
  reservation: NonNullable<ClientConfirmReservationMutation["clientConfirmReservation"]>,
): EventPurchase["values"] {
  // items price * quantity should equal to value;
  return {
    transaction_id: reservation.package.id,
    value: reservation.package.serviceBillingValue * multiplier,
    currency: "SGD",
    is_first_booking: reservation.isFirstBooking,
    items: Array(multiplier).fill({
      item_id: reservation.package.packageDetail.id,
      item_category: reservation.package.packageDetail.serviceType,
      price: reservation.package.serviceBillingValue,
      quantity: 1,
    }) as Item[],
  };
}

export async function buildLinkWithTrackingUrl(url: string) {
  const { distinct_id, session_id } = PosthogAnalytics.getIdentifiers();
  const anonymousId = await rudderanalytics.getAnonymousId();
  const gaLinker = await getGALinkerParam();

  const urlObject = new URL(url);

  if (distinct_id) urlObject.searchParams.set("distinct_id", distinct_id);
  if (session_id) urlObject.searchParams.set("session_id", session_id);
  if (anonymousId) urlObject.searchParams.set("ajs_aid", anonymousId);
  if (gaLinker) urlObject.searchParams.set("_gl", gaLinker);

  return urlObject.toString();
}
